/**
 * What one Availability grid cell reports, for a leaf row or a whole group.
 *
 * Every callback on that column — value, group aggregate, filter, sort, renderer — routes through
 * it. Leaf and group rows differ only in the entries handed in.
 */
import { availabilityForKeys } from "~/utils/stockProjection"
import { filterDefined } from "~/utils/tsUtils"

import type { AvailabilityByColumn } from "~/hooks/useAvailabilityFromProjection"

/** The column the cell sits in. Preorders are bucketed by its half-open `[firstDay, lastDay)`. */
export type AvailabilityColumn = { id: string; firstDay: Date; lastDay: Date }

/** The projection maps the grid already holds, so the cell never issues its own query. */
export type AvailabilityProjection = {
  byColumn: AvailabilityByColumn
  horizonByColumn: Map<string, (keyIds: string[]) => boolean>
}

export type PreorderEntry = {
  warehouseId: string
  deliveryDate: Date
  displayColor: string
  unfulfillableQty: number
  atRiskQty: number
}

/** The slice of a row the cell reads. Deliberately narrower than `IVariantSummary`. */
export type AvailabilityRow = {
  variantId?: string | undefined
  skuId?: string | undefined
  futureKnownPreorders?: PreorderEntry[] | undefined
}

export type AvailabilityCell = {
  /**
   * Whole-number percentage from `availabilityPercent`, or `undefined` when the period had no
   * demand to measure against.
   */
  value: number | undefined
  /** Every key behind the cell stops forecasting before this period ends. */
  unavailableForecast: boolean
  unfulfillableQty: number
  atRiskQty: number
  forecast: number
  stockoutLoss: number
}

/**
 * The whole-number percentage that availability is shown, sorted and filtered as, everywhere.
 *
 * Plain rounding would print 99.6% as 100% and 0.4% as 0%, and a grid filter on "100" would then
 * drop the row the user is looking at. The two ends are reserved instead: 100 means no projected
 * lost sales at all, 0 means nothing realized at all, and any ratio strictly between them lands
 * in 1..99 after rounding.
 */
export function availabilityPercent(ratio: number): number {
  if (ratio >= 1) return 100
  if (ratio <= 0) return 0
  return Math.min(99, Math.max(1, Math.round(ratio * 100)))
}

/**
 * Sums the row's keys and divides once, then adds the preorder quantities landing in this column.
 *
 * A row is keyed by its own skuId when it has one, otherwise by its variantId, so the same function
 * serves a size row reading SKU-grain pairs and every row above it reading variant-grain ones.
 *
 * `atRiskQty` arrives inclusive of `unfulfillableQty`, so the at-risk figure reported here is the
 * remainder — the two are rendered side by side and would otherwise double-count.
 */
export function availabilityCellValue(
  entries: AvailabilityRow[],
  column: AvailabilityColumn,
  projection: AvailabilityProjection,
  preorders: { enabled: boolean; warehouseId: string | undefined },
): AvailabilityCell {
  const keyIds = filterDefined(entries.map((e) => e.skuId ?? e.variantId))
  const { demand, lostSales, availability } = availabilityForKeys(
    projection.byColumn.get(column.id),
    keyIds,
  )

  let unfulfillableQty = 0
  let atRiskQty = 0
  if (preorders.enabled) {
    for (const entry of entries) {
      for (const po of entry.futureKnownPreorders ?? []) {
        if (!po || po.displayColor === "none") continue
        if (preorders.warehouseId && po.warehouseId !== preorders.warehouseId) continue
        if (po.deliveryDate < column.firstDay || po.deliveryDate >= column.lastDay) continue
        unfulfillableQty += po.unfulfillableQty
        atRiskQty += po.atRiskQty - po.unfulfillableQty
      }
    }
  }

  return {
    value: availability.kind === "ratio" ? availabilityPercent(availability.value) : undefined,
    unavailableForecast: projection.horizonByColumn.get(column.id)?.(keyIds) ?? false,
    unfulfillableQty,
    atRiskQty,
    forecast: demand,
    stockoutLoss: lostSales,
  }
}
